import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import qs from 'query-string';

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import {
  login,
  logout,
  resetLoginRequest,
} from '@plone/volto/actions/userSession/userSession';
import { purgeMessages } from '@plone/volto/actions/messages/messages';
import AppNav from '../AppNav/AppNav';

const messages = defineMessages({
  login: {
    id: 'Log in',
    defaultMessage: 'Log in',
  },
  loginTitle: {
    id: 'Patent manager login title',
    defaultMessage: 'Sign in to continue your work.',
  },
  loginNote: {
    id: 'Patent manager login note',
    defaultMessage:
      'Use the same account you use for the patent manager overview, matters, jobs, and OA workspace.',
  },
  loginName: {
    id: 'Login Name',
    defaultMessage: 'Login Name',
  },
  password: {
    id: 'Password',
    defaultMessage: 'Password',
  },
  loginFailed: {
    id: 'Login Failed',
    defaultMessage: 'Login failed. Check your username and password.',
  },
  forgotPassword: {
    id: 'box_forgot_password_option',
    defaultMessage: 'Forgot your password?',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  authentication: {
    id: 'Authentication',
    defaultMessage: 'Authentication',
  },
  signingIn: {
    id: 'Signing in',
    defaultMessage: 'Signing in…',
  },
  workspace: {
    id: 'Workspace',
    defaultMessage: 'Workspace',
  },
  focusTitle: {
    id: 'Patent manager focus',
    defaultMessage: 'Daily work, matters, and OA drafting in one place.',
  },
  focusDetail: {
    id: 'Patent manager login detail',
    defaultMessage:
      'After login, you will return to your requested page or to Today by default.',
  },
  focusToday: {
    id: 'Today view for current priorities',
    defaultMessage: 'Today view for current priorities',
  },
  focusTracking: {
    id: 'Matter and job tracking',
    defaultMessage: 'Matter and job tracking',
  },
  focusOa: {
    id: 'OA translation and drafting workspace',
    defaultMessage: 'OA translation and drafting workspace',
  },
});

export default function LoginPage(props) {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const error = useSelector((state) => state.userSession.login.error);
  const loading = useSelector((state) => state.userSession.login.loading);
  const returnUrl =
    qs.parse(props.location?.search ?? location.search).return_url ||
    '/today';
  const previousToken = usePrevious(token);

  useEffect(() => {
    document.body.classList.add('patent-dashboard-page');
    return () => {
      document.body.classList.remove('patent-dashboard-page');
    };
  }, []);

  useEffect(() => {
    if (location?.state?.isLogout) {
      dispatch(logout());
      dispatch(purgeMessages());
      history.push(`${location.pathname}${location.search}`);
    } else if (token && token !== previousToken) {
      history.push(returnUrl || '/today');
    }

    return () => {
      dispatch(resetLoginRequest());
    };
  }, [
    dispatch,
    history,
    location.pathname,
    location.search,
    location?.state?.isLogout,
    previousToken,
    returnUrl,
    token,
  ]);

  const onLogin = (event) => {
    event.preventDefault();
    dispatch(
      login(
        document.getElementsByName('login')[0].value,
        document.getElementsByName('password')[0].value,
      ),
    );
  };

  return (
    <main className="patent-section patent-login">
      <Helmet title={intl.formatMessage(messages.login)} />
      <section className="patent-section__shell">
        <header className="patent-section__hero">
          <AppNav currentPath="" />
          <p className="patent-section__eyebrow">
            {intl.formatMessage(messages.authentication)}
          </p>
          <h1>{intl.formatMessage(messages.loginTitle)}</h1>
          <p className="patent-section__note">
            {intl.formatMessage(messages.loginNote)}
          </p>
        </header>

        <div className="patent-login__layout">
          <section className="patent-login__panel patent-section__card">
            <div className="patent-dashboard__section-header">
              <h2>
                {intl.formatMessage(messages.login)}
              </h2>
            </div>

            <form className="patent-login__form" method="post" onSubmit={onLogin}>
              <label className="patent-entry__field" htmlFor="login">
                <span className="patent-entry__label">
                  {intl.formatMessage(messages.loginName)}
                </span>
                <input
                  autoComplete="username"
                  autoFocus
                  className="patent-entry__input"
                  id="login"
                  name="login"
                  placeholder={intl.formatMessage(messages.loginName)}
                  type="text"
                />
              </label>

              <label className="patent-entry__field" htmlFor="password">
                <span className="patent-entry__label">
                  {intl.formatMessage(messages.password)}
                </span>
                <input
                  autoComplete="current-password"
                  className="patent-entry__input"
                  id="password"
                  name="password"
                  placeholder={intl.formatMessage(messages.password)}
                  type="password"
                />
              </label>

              {error ? (
                <p className="patent-entry__error">
                  {intl.formatMessage(messages.loginFailed)}
                </p>
              ) : null}

              <div className="patent-login__actions">
                <button
                  className="patent-entry__submit"
                  id="login-form-submit"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    intl.formatMessage(messages.signingIn)
                  ) : (
                    intl.formatMessage(messages.login)
                  )}
                </button>
                <Link
                  className="patent-entry__ghost"
                  id="login-form-cancel"
                  to={returnUrl || '/today'}
                >
                  {intl.formatMessage(messages.cancel)}
                </Link>
              </div>

              <div className="patent-login__links">
                <Link className="patent-login__link" to="/passwordreset">
                  {intl.formatMessage(messages.forgotPassword)}
                </Link>
              </div>
            </form>
          </section>

          <aside className="patent-login__panel patent-section__card patent-login__panel--aside">
            <p className="patent-section__eyebrow">
              {intl.formatMessage(messages.workspace)}
            </p>
            <h2>
              {intl.formatMessage(messages.focusTitle)}
            </h2>
            <p className="patent-section__note">
              {intl.formatMessage(messages.focusDetail)}
            </p>
            <ul className="patent-login__list">
              <li>{intl.formatMessage(messages.focusToday)}</li>
              <li>{intl.formatMessage(messages.focusTracking)}</li>
              <li>{intl.formatMessage(messages.focusOa)}</li>
            </ul>
          </aside>
        </div>
      </section>
    </main>
  );
}
